import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Skeleton } from 'components/ui/skeleton';
import { Doughnut } from 'react-chartjs-2';
import { Users } from '@phosphor-icons/react';
import { formatCurrency } from 'shared/utils/currency';
import { AcquisitionResponsibleBucket } from '../types/statistics.types';
import { CHART_COLORS, CHART_COLORS_ALPHA, COMMON_TOOLTIP } from './chartConfig';
import { MetricInfo } from './MetricInfo';
import './chartConfig';

interface Props {
  data?: AcquisitionResponsibleBucket[];
  loading: boolean;
}

export const ResponsibleDistributionChart = ({ data, loading }: Props) => {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null;
    const sorted = [...data].sort((a, b) => b.count - a.count);
    return {
      labels: sorted.map((d) => d.displayName || d.userId),
      datasets: [
        {
          label: 'Dossiers',
          data: sorted.map((d) => d.count),
          backgroundColor: CHART_COLORS_ALPHA.slice(0, sorted.length),
          borderColor: CHART_COLORS.slice(0, sorted.length),
          borderWidth: 2,
        },
      ],
      _meta: sorted,
    };
  }, [data]);

  if (loading) {
    return (
      <Card className="bg-white">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-moss-primary" weight="duotone" />
            <Skeleton className="h-5 w-40" />
          </div>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[320px] w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-moss-primary" weight="duotone" />
          <CardTitle className="text-lg">Dossiers per verantwoordelijke</CardTitle>
          <MetricInfo
            description={
              <div className="space-y-1">
                <p>
                  Aantal dossiers per <code>yallyResponsibleUserId</code>. Namen
                  worden server-side opgehaald via Clerk.
                </p>
                <p>
                  Hover voor aantal deals, afgewezen, volume en gemiddelde yield
                  per teamlid.
                </p>
                <p>Dossiers zonder verantwoordelijke staan onder "Niet toegewezen".</p>
              </div>
            }
          />
        </div>
        <p className="text-xs text-neutral-secondary-mid">
          Verdeling van acquisities per teamlid
        </p>
      </CardHeader>
      <CardContent>
        <div className="h-[320px]">
          {chartData ? (
            <Doughnut
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'right',
                    labels: { boxWidth: 12, padding: 12, font: { size: 12 } },
                  },
                  tooltip: {
                    ...COMMON_TOOLTIP,
                    callbacks: {
                      label: (ctx: any) => {
                        const idx = ctx.dataIndex;
                        const meta = chartData._meta[idx];
                        const lines = [
                          `${ctx.parsed} dossiers`,
                          `${meta.deals} deals · ${meta.rejected} afgewezen`,
                          `Volume: ${formatCurrency(meta.totalVolume)}`,
                        ];
                        if (meta.avgYield > 0) {
                          lines.push(
                            `Gem. yield: ${(meta.avgYield * 100).toFixed(2)}%`,
                          );
                        }
                        return lines;
                      },
                    },
                  },
                },
              }}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-neutral-secondary-mid">
              Geen data beschikbaar
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
